import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { X, Send, Bot, User } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { fetchGVIntelligence } from '@/lib/gvIntelligence';

interface Source {
  index: string;
  source: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: Source[];
}

const QUICK_ACTIONS = [
  { label: 'Actieve projecten', message: 'Overzicht van alle actieve projecten' },
  { label: 'Open opportunities', message: 'Welke opportunities staan open?' },
  { label: 'Leveranciers', message: 'Wie zijn onze leveranciers?' },
  { label: 'BESS status', message: 'Status van de BESS projecten' },
];

export const CRMChatbot: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'assistant',
      content: 'Hallo! Ik ben **GV Intelligence**. Ik kan je helpen met:\n\n• **Projecten** - Status, overzichten, details\n• **Contacten** - Leveranciers, klanten, partners\n• **Opportunities** - Pipeline, offertes, deals\n• **Data** - Subsidies, BESS, locaties\n\nStel me een vraag!',
      timestamp: new Date()
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async (messageText?: string) => {
    const text = messageText || currentMessage;
    if (!text.trim() || !user || isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: text.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsLoading(true);

    try {
      const data = await fetchGVIntelligence(text.trim());

      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: data.answer || 'Geen antwoord ontvangen.',
        timestamp: new Date(),
        sources: data.sources || [],
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error: any) {
      console.error('Error sending message:', error);
      
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: `Sorry, er ging iets mis: ${error.message}. Probeer het opnieuw.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
      
      toast({
        title: 'Verbinding mislukt',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatMessage = (content: string) => {
    return content
      .replace(/^### (.*$)/gm, '<h3 class="font-semibold text-sm mt-3 mb-1">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="font-semibold text-base mt-3 mb-1">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/^[•-] (.*$)/gm, '<li class="ml-4 list-disc text-sm">$1</li>')
      .replace(/^\d+\. (.*$)/gm, '<li class="ml-4 list-decimal text-sm">$1</li>')
      .replace(/\n/g, '<br />');
  };

  if (!user) return null;

  const chatbotContent = (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-[9999] overflow-hidden p-0 bg-background border-2 border-primary/20 hover:border-primary/40 hover:scale-105"
          size="icon"
        >
          <img
            src="https://fxlorumfwzrdwiftbalo.supabase.co/storage/v1/object/sign/pictures/Ontwerp%20zonder%20titel-144.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lNGNhM2Q4My02YTA4LTQwNmYtYWI4OC0xNmEyZTA3Zjk5M2YiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJwaWN0dXJlcy9PbnR3ZXJwIHpvbmRlciB0aXRlbC0xNDQucG5nIiwiaWF0IjoxNzU4MDEyOTA0LCJleHAiOjEwMzk3OTI2NTA0fQ.MULGOmZsziMLugBqH2awo1FtvPBOO5c3MRExodH5poI"
            alt="GV Intelligence"
            className="h-full w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              e.currentTarget.parentElement!.innerHTML = '<svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path></svg>';
            }}
          />
        </Button>
      )}

      {isOpen && (
        <Card className="fixed bottom-6 right-6 w-[420px] h-[540px] shadow-2xl z-[9999] flex flex-col overflow-hidden border-0">
          <CardHeader className="bg-[#014336] text-white p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
                  <img
                    src="https://fxlorumfwzrdwiftbalo.supabase.co/storage/v1/object/sign/pictures/Ontwerp%20zonder%20titel-144.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9lNGNhM2Q4My02YTA4LTQwNmYtYWI4OC0xNmEyZTA3Zjk5M2YiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJwaWN0dXJlcy9PbnR3ZXJwIHpvbmRlciB0aXRlbC0xNDQucG5nIiwiaWF0IjoxNzU4MDEyOTA0LCJleHAiOjEwMzk3OTI2NTA0fQ.MULGOmZsziMLugBqH2awo1FtvPBOO5c3MRExodH5poI"
                    alt="Assistant"
                    className="h-8 w-8 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-base">GV Intelligence</h3>
                  <p className="text-xs text-white/70">LightRAG · Altijd beschikbaar</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/20"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 flex flex-col p-0 bg-muted/30 overflow-hidden">
            <div className="flex-1 overflow-y-auto px-4 py-4">
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${
                      message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                    }`}
                  >
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
                      message.role === 'assistant' 
                        ? 'bg-[#014336]/10' 
                        : 'bg-secondary'
                    }`}>
                      {message.role === 'assistant' ? (
                        <Bot className="h-4 w-4 text-[#014336]" />
                      ) : (
                        <User className="h-4 w-4 text-secondary-foreground" />
                      )}
                    </div>
                    
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                        message.role === 'user'
                          ? 'bg-[#014336] text-white rounded-br-md'
                          : 'bg-background border border-border rounded-bl-md'
                      }`}
                    >
                      <div
                        className="leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: formatMessage(message.content)
                        }}
                      />
                      {/* Sources */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/10">
                          <div className="text-[10px] font-semibold text-[#014336]/60 mb-1 font-mono tracking-wider">BRONNEN</div>
                          <div className="flex flex-wrap gap-1">
                            {message.sources.slice(0, 5).map((s, i) => (
                              <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[#014336]/5 text-[#014336]/70 font-mono">
                                [{s.index}] {s.source}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div
                        className={`text-[10px] mt-2 ${
                          message.role === 'user' ? 'text-white/60 text-right' : 'text-muted-foreground'
                        }`}
                      >
                        {message.timestamp.toLocaleTimeString('nl-NL', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex gap-3">
                    <div className="h-8 w-8 rounded-full bg-[#014336]/10 flex items-center justify-center shrink-0">
                      <Bot className="h-4 w-4 text-[#014336]" />
                    </div>
                    <div className="bg-background border border-border rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="h-2 w-2 rounded-full bg-[#014336]/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="h-2 w-2 rounded-full bg-[#014336]/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="h-2 w-2 rounded-full bg-[#014336]/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </div>

            {messages.length <= 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2">
                {QUICK_ACTIONS.map((action, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="text-xs rounded-full h-7 border-[#014336]/20 hover:bg-[#014336]/10 hover:border-[#014336]/40 transition-colors"
                    onClick={() => sendMessage(action.message)}
                    disabled={isLoading}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}

            <div className="border-t border-border bg-background p-3">
              <div className="flex items-center gap-2 bg-muted/50 rounded-xl p-1.5 pl-3">
                <Input
                  ref={inputRef}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Stel een vraag..."
                  disabled={isLoading}
                  className="flex-1 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground/60 h-9 text-sm"
                />
                <Button
                  onClick={() => sendMessage()}
                  disabled={!currentMessage.trim() || isLoading}
                  size="icon"
                  className="h-9 w-9 rounded-full shrink-0 bg-[#014336] hover:bg-[#014336]/90"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  return createPortal(chatbotContent, document.body);
};
